import React, { useEffect, useId, useRef, useState } from "react";
import { MdAdd } from "react-icons/md";
import tagsService from "services/tags.service";
import { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { palette } from "lib/chartTheme";
import Button from "components/button";
import InputField from "components/fields/InputField";
import Loader from "components/loader/Loader";
import { EmptyState } from "components/states";
import TagBadge from "./TagBadge";

// Tag colors are stored with the tag, so new tags pick from the shared data palette.
const TAG_COLORS = [
  { name: "Sage", value: palette.sage },
  { name: "Deep Green", value: palette.deep },
  { name: "Sky", value: palette.sky },
  { name: "Slate", value: palette.slate },
  { name: "Gold", value: palette.gold },
  { name: "Rose", value: palette.rose },
];
const DEFAULT_COLOR = palette.sky;

/**
 * Tag picker for a person. With `userId` each add/remove is saved immediately;
 * without it (create form) changes stay local until the form is saved.
 *
 * @param {object} props
 * @param {string|number} [props.userId]
 * @param {Array} [props.selectedTags=[]]
 * @param {(tags: Array) => void} props.onTagsChange
 * @param {boolean} [props.editable=true] When false, renders the tags read-only.
 */
const TagSelector = ({ userId, selectedTags = [], onTagsChange, editable = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(DEFAULT_COLOR);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const panelId = useId();

  useEffect(() => {
    const loadTags = async () => {
      try {
        setLoading(true);
        const response = await tagsService.getTags({ isActive: true });
        setAvailableTags(response.data || []);
      } catch (error) {
        console.error("Error loading tags:", error);
        notify.error(getApiErrorMessage(error, "Could not load tags"));
      } finally {
        setLoading(false);
      }
    };
    loadTags();
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setCreating(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setCreating(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleAddTag = async (tag) => {
    if (!userId) {
      onTagsChange([...selectedTags, tag]);
      setIsOpen(false);
      return;
    }

    try {
      await tagsService.assignTagToUsers(tag.id, [userId]);
      onTagsChange([...selectedTags, tag]);
      setIsOpen(false);
      notify.success(`Tag "${tag.name}" added`);
    } catch (error) {
      console.error("Error adding tag:", error);
      notify.error(getApiErrorMessage(error, "Could not add tag"));
    }
  };

  const handleRemoveTag = async (tag) => {
    if (!userId) {
      onTagsChange(selectedTags.filter((t) => t.id !== tag.id));
      return;
    }

    try {
      await tagsService.removeTagFromUser(tag.id, userId);
      onTagsChange(selectedTags.filter((t) => t.id !== tag.id));
      notify.success(`Tag "${tag.name}" removed`);
    } catch (error) {
      console.error("Error removing tag:", error);
      notify.error(getApiErrorMessage(error, "Could not remove tag"));
    }
  };

  const resetCreate = () => {
    setCreating(false);
    setNewTagName("");
    setNewTagColor(DEFAULT_COLOR);
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      notify.error("Tag name is required");
      return;
    }

    try {
      setSaving(true);
      const response = await tagsService.createTag({
        name: newTagName.trim(),
        color: newTagColor,
        type: "custom",
      });

      const newTag = response.data;
      setAvailableTags([...availableTags, newTag]);
      resetCreate();
      notify.success(`Tag "${newTag.name}" created`);

      handleAddTag(newTag);
    } catch (error) {
      console.error("Error creating tag:", error);
      notify.error(getApiErrorMessage(error, "Could not create tag"));
    } finally {
      setSaving(false);
    }
  };

  const filteredTags = availableTags.filter(
    (tag) =>
      !selectedTags.some((st) => st.id === tag.id) &&
      tag.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!editable) {
    return (
      <div className="flex flex-wrap gap-2">
        {selectedTags.length > 0 ? (
          selectedTags.map((tag) => <TagBadge key={tag.id} tag={tag} size="sm" />)
        ) : (
          <span className="text-sm text-fg-muted">No tags</span>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {selectedTags.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {selectedTags.map((tag) => (
            <TagBadge key={tag.id} tag={tag} onRemove={handleRemoveTag} />
          ))}
        </div>
      )}

      <Button
        ref={triggerRef}
        variant="secondary"
        size="sm"
        leftIcon={<MdAdd className="h-4 w-4" />}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
        onClick={() => setIsOpen((open) => !open)}
      >
        Add Tag
      </Button>

      {isOpen && (
        <div
          id={panelId}
          className="absolute z-30 mt-2 w-80 max-w-full overflow-hidden rounded-card border border-line bg-surface shadow-overlay"
        >
          <div className="border-b border-line p-3">
            <InputField
              type="search"
              aria-label="Search tags"
              placeholder="Search tags"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <Loader size="sm" label="Loading tags" className="py-6" />
            ) : filteredTags.length > 0 ? (
              <ul className="p-2">
                {filteredTags.map((tag) => (
                  <li key={tag.id}>
                    <button
                      type="button"
                      onClick={() => handleAddTag(tag)}
                      className="flex w-full items-center justify-between gap-2 rounded-control px-3 py-2 text-left transition-colors hover:bg-surface-subtle focus-ring"
                    >
                      <span className="flex items-center gap-2">
                        <span
                          aria-hidden="true"
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: tag.color }}
                        />
                        <span className="text-sm font-medium text-fg">{tag.name}</span>
                        {tag.type === "predefined" && (
                          <span className="text-xs text-fg-muted">Predefined</span>
                        )}
                      </span>
                      <MdAdd aria-hidden="true" className="h-4 w-4 text-fg-muted" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                variant="inline"
                title={searchQuery ? "No matching tags found" : "No tags available"}
              />
            )}
          </div>

          {creating ? (
            <div className="flex flex-col gap-3 border-t border-line bg-surface-raised p-3">
              <InputField
                label="Tag Name"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCreateTag();
                  }
                }}
              />
              <fieldset>
                <legend className="mb-1.5 text-sm font-medium text-fg">Color</legend>
                <div className="flex flex-wrap gap-2">
                  {TAG_COLORS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      aria-label={color.name}
                      aria-pressed={newTagColor === color.value}
                      title={color.name}
                      onClick={() => setNewTagColor(color.value)}
                      className={`h-6 w-6 rounded-full border-2 transition-transform focus-ring ${
                        newTagColor === color.value
                          ? "scale-110 border-fg"
                          : "border-transparent"
                      }`}
                      style={{ backgroundColor: color.value }}
                    />
                  ))}
                </div>
              </fieldset>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" size="sm" onClick={resetCreate}>
                  Cancel
                </Button>
                <Button size="sm" loading={saving} onClick={handleCreateTag}>
                  Create Tag
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="flex w-full items-center gap-2 border-t border-line bg-surface-raised p-3 text-left text-sm font-medium text-fg transition-colors hover:bg-surface-subtle focus-ring"
            >
              <MdAdd aria-hidden="true" className="h-4 w-4" />
              Create New Tag
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TagSelector;
